type ChromeRuntime = {
  lastError?: { message?: string };
  sendMessage(extensionId: string, message: unknown, callback: (response: unknown) => void): void;
  sendMessage(extensionId: string, message: unknown): Promise<unknown>;
};

const EXTENSION_PING_TIMEOUT_MS = 1500;

// Chrome exposes `chrome.runtime` to a page only while some installed extension lists it in `externally_connectable`.
export function getChromeRuntime(): ChromeRuntime | undefined {
  return (globalThis as { chrome?: { runtime?: ChromeRuntime } }).chrome?.runtime;
}

const isPong = (response: unknown) =>
  typeof response === 'object' && response !== null && (response as { action?: unknown }).action === 'pong';

/**
 * Asks an extension that lists this origin in `externally_connectable` whether it is installed: `{ action: 'ping' }`
 * must be answered with `{ action: 'pong' }`. Resolves false (never rejects) without a runtime, on a messaging error
 * (the extension is missing or disabled) or when no answer arrives in time.
 */
export const pingExtension = (extensionId: string, timeoutMs = EXTENSION_PING_TIMEOUT_MS): Promise<boolean> => {
  const runtime = getChromeRuntime();

  if (typeof runtime?.sendMessage !== 'function') return Promise.resolve(false);

  return new Promise((resolve) => {
    const timer = setTimeout(() => resolve(false), timeoutMs);
    const settle = (isInstalled: boolean) => {
      clearTimeout(timer);
      resolve(isInstalled);
    };

    try {
      runtime.sendMessage(extensionId, { action: 'ping' }, (response) => {
        // Reading lastError marks it handled, so Chrome does not log "Unchecked runtime.lastError".
        settle(!runtime.lastError && isPong(response));
      });
    } catch {
      settle(false);
    }
  });
};
